// Phase 2: Focus Selector Component
// Segmented control for selecting report focus type

import React from 'react';
import { Building2, User, Music, Target } from 'lucide-react';
import type { ReportFocus } from '../../../shared/types/reports';

interface FocusSelectorProps {
  value: ReportFocus;
  onChange: (focus: ReportFocus) => void;
  disabled?: boolean;
}

const FOCUS_OPTIONS = [
  { value: 'client' as const, label: 'Client', icon: Building2 },
  { value: 'artist' as const, label: 'Artist', icon: User },
  { value: 'release' as const, label: 'Release', icon: Music },
  { value: 'campaign' as const, label: 'Campaign', icon: Target },
];

export const FocusSelector: React.FC<FocusSelectorProps> = ({ value, onChange, disabled = false }) => {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Report Focus
      </label>
      <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-1" role="tablist" aria-label="Report Focus">
        {FOCUS_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.value;
          
          return (
            <button
              key={option.value}
              role="tab"
              aria-selected={isSelected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={`
                inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                transition-all duration-200
                ${isSelected
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <Icon className="h-4 w-4" />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
